<template>
  <v-menu offset-x :nudge-right="16" transition="slide-x-transition" :min-width="240">
    <template #activator="{ on }">
      <NavItem data-test="nav-tab-Account" icon="account_circle" label="Account" v-on="on" />
    </template>

    <v-card>
      <v-card-text>
        <vs-heading>{{ userName }}</vs-heading>
        <vs-text type="caption">{{ userCompany }}</vs-text>
        <vs-text type="caption">{{ user.displayedRole }}</vs-text>

        <v-row v-if="user.email" class="mt-1" align="center">
          <v-col class="shrink pr-0">
            <v-icon>email</v-icon>
          </v-col>
          <v-col>{{ user.email }}</v-col>
        </v-row>
      </v-card-text>
      <v-divider />

      <v-list v-if="!loading">
        <v-list-item
          v-for="action in getItems"
          :key="action.label"
          active-class=""
          :href="action.href"
          :to="action.to"
          :target="action.target"
          @click="action.onClick && action.onClick()"
          ripple
        >
          <v-list-item-content>
            <v-list-item-title data-test="nav-account-item">
              {{ action.label }}
            </v-list-item-title>
          </v-list-item-content>
          <v-list-item-action v-if="action.icon">
            <v-icon>{{ action.icon }}</v-icon>
          </v-list-item-action>
        </v-list-item>
      </v-list>
    </v-card>
  </v-menu>
</template>

<script setup lang="ts">
import { useGetters } from 'vuex-composition-helpers'
import NavItem from './NavItem.vue'
import { computed, ComputedRef } from 'vue'
import { getInstance } from '@/auth/auth.js'
import { getAdminPortalConfig } from '@/api/config'
import moment from 'moment'
import { useQuery } from '@tanstack/vue-query'
import CurrentUserContext from '@/models/current-user-context'
import { useRoute } from 'vue-router/composables'

interface Actions {
  label: string
  href?: string
  target?: string
  to?: string
  icon?: string
  onClick?: () => void
}

const {
  getContext: getUserContext,
  hasAccountOrganizationAccess,
  isGlobalAdmin,
} = useGetters('permissions', ['getContext', 'hasAccountOrganizationAccess', 'isGlobalAdmin'])

const auth = getInstance()

const route = useRoute()

const { data: config, isFetching } = useQuery({
  queryKey: ['config'],
  queryFn: getAdminPortalConfig,
  cacheTime: Infinity,
  staleTime: moment.duration(1, 'days').asMilliseconds(),
})

const adminPortalUrl = computed(() => {
  if (config.value) {
    const url = new URL(`${config.value.data.url}/Account/Login`)
    url.searchParams.append('tenant', auth.tenant)
    url.searchParams.append('organization', auth.organizationId)
    return url.href
  }
  return ''
})

const switchAccount = () => {
  const targetUrl = getClosestApproximateRoute()
  return auth.loginWithRedirect({ appState: { targetUrl } })
}

const getItems: ComputedRef<Array<Actions>> = computed(() => {
  return actions.value
})

const loading: ComputedRef<boolean> = computed(() => {
  return auth.loading || isFetching.value
})

const user: ComputedRef<CurrentUserContext> = computed(() => {
  return getUserContext.value
})

const userName: ComputedRef<string> = computed(() => {
  const name = getUserContext.value.name
  if (name != null) return name

  if (isGlobalAdmin.value) {
    return 'Global Admin'
  }

  return ''
})

const userCompany: ComputedRef<string> = computed(() => {
  const orgPaths = getUserContext.value.lineage
    .filter((l) => l.type === 'Organization')
    .map((l) => l.path)
  return orgPaths ? orgPaths[0] : null
})

const actions = computed(() => {
  const items: Array<Actions> = []

  if (hasAccountOrganizationAccess.value) {
    items.push({
      label: 'Admin Portal',
      href: adminPortalUrl.value,
      target: '_blank',
    })
  }

  if (isGlobalAdmin.value) {
    items.push({
      label: 'Switch Org',
      icon: 'swap_horiz',
      to: '#',
      onClick: switchAccount,
    })
  }

  items.push({
    label: 'Logout',
    icon: 'power_settings_new',
    to: `/logout`,
  })

  return items
})

const getClosestApproximateRoute = () => {
  const baseRoutes = [
    '/map',
    '/schedule/day',
    '/schedule/week',
    '/jobs',
    '/form-submissions',
    '/form-builder',
    '/analytics',
  ]

  for (const baseRoute of baseRoutes) {
    if (route.path.startsWith(baseRoute)) {
      return baseRoute
    }
  }

  return '/'
}
</script>
